import React, { useEffect, useState } from 'react';
import { Spin, Typography } from 'antd';
import useDebounce from '../../utils/useDebounce.js';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import style from './Announcements.module.scss';
import { setBackButton, setPageTitle } from '../../features/header/headerSlice';
import { Panel, PanelBody } from '../../components/panel/panel';
import { Row, Col, Form, Input, Skeleton, Table, Space, Button, Empty } from 'antd';
import FilterWrapper from '../../features/filterWrapper/FilterWrapper';
import Multiselect from '../../features/multiselect/Multiselect.js';
import { getAnnouncementDataColumns } from './constant.js';
import { confirmationModal } from '../../features/confirmationModal/confirmationModal.js';
import { PlusOutlined } from '@ant-design/icons';
import { ResponsiveButton } from '../../features/responsive/ResponsiveButton.js';
import AnnouncementDetailModal from './AnnouncementDetailModal.js';
import { useDeleteAnnouncementMutation, useGetAnnouncementsQuery, ANNOUNCEMENT_STATUS } from '../../features/announcement/AnnouncementApi.js';
import { executeSingleAction } from '../../utils/ApiResponseHandler.js';
import { BellFilled } from '@ant-design/icons';
import AnnouncementViewModal from './AnnouncementViewModal.js';
const { Search } = Input;
const { Text } = Typography;

const Announcements = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pageTitle = t('Announcement.Title');
  const [filterForm] = Form.useForm();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const { data: announcements, isFetching } = useGetAnnouncementsQuery();
  const [deletAnnouncement] = useDeleteAnnouncementMutation();
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(false);
  const [filteredAnnouncement, setFilteredAnnouncement] = useState([]);
  const [filteredStatus, setFilteredStatus] = useState([]);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    dispatch(setBackButton(true));
    dispatch(setPageTitle(pageTitle));
  }, [pageTitle, dispatch]);

  useEffect(() => {
    if (isFetching) return;

    let filtered = announcements || [];
    if (debouncedSearchText && debouncedSearchText.trim() !== "") {
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(debouncedSearchText.toLowerCase())
      );
    }
    if (filteredStatus.length > 0) {
      filtered = filtered.filter(a => filteredStatus.includes(a.status));
    }
    setFilteredAnnouncement(filtered);
  }, [announcements, debouncedSearchText, isFetching, filteredStatus]);

  const onFilterChange = (value) => {
    setFilteredStatus(value);
  };

  const onEdit = (v) => {
    setSelectedAnnouncement(v);
    setShowDetailModal(true);
  }

  const onDelete = (v) => {
    confirmationModal(
      t('Announcement.DeleteAnnouncementTitle', { title: v?.title }),
      t('Announcement.DeleteAnnouncementDescription', { title: v?.title }),
      t('Common.Delete'),
      t('Common.CancelButton'),
      () => {
        const payload = {
          successMessage: t("Announcement.AnnouncementDeleted"),
          id: v.id,
          identifier: "Announcement" + v.id,
          loadingMessage: t("Common.Saving")
        }
        executeSingleAction(payload, deletAnnouncement, dispatch);
      },
      'delete'
    );
  }

  const onView = (v) => {
    setSelectedAnnouncement(v);
    setShowPreviewModal(true);
  }

  return (
    <Spin spinning={isFetching}>
        {(announcements || []).length > 0 ? <Panel noPadding style={{ marginBottom: "15px" }}><PanelBody>
          <Form form={filterForm} layout="inline" style={{ background: "#f2f2f2" }}>
            <Row className={`${style.fullWidthRow} ${style.searchPanelRow}`}>
              <Col xs={{ span: 24 }}>
                <FilterWrapper size="medium" custom={{ 0: "large" }} rightChild={<div style={{ display: "flex", justifyContent: "end", margin: "0px 16px" }}>
                  <Space>
                    <ResponsiveButton
                      onClick={() => { setShowDetailModal(true); setSelectedAnnouncement(undefined); }}
                      title={t("Announcement.AddNew")}
                      icon={<PlusOutlined />} />

                  </Space>
                </div>}>
                  <Form.Item name="keyword" style={{ margin: 0 }}>
                    <Search placeholder={t('Announcement.Searchkeyword')} onChange={(e)=>{setSearchText(e.target.value)}} />
                  </Form.Item>
                  <Form.Item name="status" style={{ margin: 0 }}>
                    <Multiselect
                      options={Object.entries(ANNOUNCEMENT_STATUS).map(([key, value]) => ({
                        value: Number(key),
                        label: t(`Announcement.Status.${value}`)
                      }))}
                      onFilterChange={onFilterChange}
                      placeholder={t("Announcement.AnnouncementStatus")} />
                </Form.Item>
                </FilterWrapper>
              </Col>
            </Row>
          </Form>
          <Row className={style.fullWidthRow}>
            <Col xs={{ span: 24 }}>
              {isFetching ? <Skeleton active /> : <Table
              tableLayout='fixed'
              rowKey="id"
              columns={getAnnouncementDataColumns(onEdit, onDelete, onView)}
              pagination={{
                showSizeChanger: true
              }}
              dataSource={filteredAnnouncement}
            />
            }
          </Col>
        </Row>
      </PanelBody>
      </Panel> :
        <div>
          <Empty style={{ padding: "60px 20px", backgroundColor: '#fff', margin:20 }}
            image={<div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#F5FFE5',
                color: '#bfbfbf',
                fontSize: 20,
                margin: '0 auto',
              }}
            >
              <BellFilled />
            </div>
            }
            description={
              <Space direction="vertical">
                <Text strong>{t("Announcement.NoActiveRecords")}</Text>
                <span style={{ color: '#8c8c8c' }}>
                  {t("Announcement.NoActiveRecordsMessage1")}
                </span>
                <span style={{ color: '#8c8c8c' }}>
                  {t("Announcement.NoActiveRecordsMessage2")}
                </span>
              </Space>
            }
          >
            <ResponsiveButton
              icon={<PlusOutlined />}
              onClick={() => { setShowDetailModal(true); setSelectedAnnouncement(undefined); }}
              title={t("Announcement.AddNew")}
              type="primary" />
          </Empty>
        </div>
      }
      <AnnouncementViewModal
        announcementDetails={[selectedAnnouncement]}
        isModalOpen={showPreviewModal}
        preview={true}
        onClose={() => {
          setSelectedAnnouncement(undefined);
          setShowPreviewModal(false);
        }}
      />
      <AnnouncementDetailModal
        announcementDetail={selectedAnnouncement}
        isModalOpen={showDetailModal}
        onClose={() => {
          setSelectedAnnouncement(undefined);
          setShowDetailModal(false);
        }}
      />
    </Spin>
  );
};

export default Announcements;